import React from 'react';
import { CheckCircle2, AlertTriangle, X, ShieldCheck } from 'lucide-react';

const ReviewBeforePublishDialog = ({
  show,
  onClose,
  onConfirmPublish,
  selectedClass,
  selectedSection,
  academicYear,
  subjectAllocations = [],
  scheduledCounts = {},
  conflictCount = 0,
  publishing = false
}) => {
  if (!show) return null;

  // Validation Checks
  const issues = [];

  // 1. Check subject requirements & over-allocations
  subjectAllocations.forEach(alloc => {
    const req = alloc.periodsPerWeek || 5;
    const subName = alloc.subjectName || alloc.subjectCode || 'Subject';
    const scheduled = scheduledCounts[subName] || 0;

    if (scheduled < req) {
      const remaining = req - scheduled;
      issues.push(`${subName} needs ${remaining} more ${remaining === 1 ? 'period' : 'periods'}`);
    } else if (scheduled > req) {
      const extra = scheduled - req;
      issues.push(`${subName} has ${extra} extra ${extra === 1 ? 'period' : 'periods'} scheduled (exceeds weekly requirement)`);
    }

    if (!alloc.teacherName || alloc.teacherName === 'Unassigned' || alloc.teacherName === 'Unassigned Faculty') {
      issues.push(`${subName} has no teacher assigned`);
    }
  });

  // 2. Check conflict count
  if (conflictCount > 0) {
    issues.push(`${conflictCount} teacher timing ${conflictCount === 1 ? 'conflict' : 'conflicts'} detected`);
  }

  const isValid = issues.length === 0;

  return (
    <div className="fs-modal-overlay" style={{ zIndex: 1200 }}>
      <div className="fs-modal-card" style={{ maxWidth: '520px', borderRadius: '16px', overflow: 'hidden' }}>
        
        {/* HEADER */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          backgroundColor: isValid ? '#F0FDF4' : '#FEF2F2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              backgroundColor: isValid ? '#DCFCE7' : '#FEE2E2',
              color: isValid ? '#166534' : '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {isValid ? <ShieldCheck size={22} /> : <AlertTriangle size={22} />}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: isValid ? '#166534' : '#991B1B' }}>
                {isValid ? 'Ready to Publish?' : 'Timetable Needs Attention'}
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: isValid ? '#15803D' : '#B91C1C' }}>
                {selectedClass} • Section {selectedSection} ({academicYear})
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div style={{ padding: '24px' }}>
          {isValid ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '13.5px', color: '#334155', lineHeight: '1.5' }}>
                Reviewing the weekly schedule for <strong>{selectedClass} Section {selectedSection}</strong>. All checks passed:
              </p>

              {[
                'All required subjects scheduled',
                'No teacher timing conflicts',
                'No subject exceeds weekly requirement',
                'Break and lunch periods protected',
                'Timetable validation passed'
              ].map((check, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#15803D', fontWeight: '600' }}>
                  <CheckCircle2 size={18} color="#166534" />
                  <span>{check}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '13.5px', color: '#334155', lineHeight: '1.5' }}>
                Please resolve the following items before publishing the timetable:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {issues.map((issue, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px',
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    color: '#991B1B',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AlertTriangle size={15} color="#DC2626" style={{ flexShrink: 0 }} />
                    <span>{issue}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justify: 'flex-end',
          gap: '12px',
          backgroundColor: '#F8FAFC'
        }}>
          {isValid ? (
            <>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '9px 18px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: 'white',
                  color: '#334155',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={onConfirmPublish}
                disabled={publishing}
                style={{
                  padding: '9px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#2563EB',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
                }}
              >
                {publishing ? 'Publishing...' : 'Publish Timetable'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#2563EB',
                color: 'white',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Fix Issues
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ReviewBeforePublishDialog;
